// /src/pages/CreateDesign.jsx
import React, { useEffect, useMemo, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { motion } from "framer-motion";
import Select from "react-select";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";

import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { useAddDesignMutation } from "../toolkitServices/designApi";

// select styles factory (red border when hasError)
const makeSelectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    height: 40,
    borderWidth: 1,
    borderColor: hasError
      ? "#ef4444" // red-500
      : state.isFocused
      ? "#9481ce"
      : "#e5e7eb",
    boxShadow: "none",
    ":hover": { borderColor: hasError ? "#ef4444" : "#9481ce" },
    borderRadius: 10,
    fontSize: "0.92rem",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  indicatorsContainer: (base) => ({ ...base, height: 40 }),
  menu: (base) => ({ ...base, borderRadius: 10, overflow: "hidden" }),
  option: (base, state) => ({
    ...base,
    padding: "8px 12px",
    backgroundColor: state.isSelected ? "#efeafd" : state.isFocused ? "#f7f6ff" : "white",
    color: "#111827",
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af" }),
  singleValue: (base) => ({ ...base, color: "#111827" }),
});

// shared input classes (toggle red styles if invalid)
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const CreateDesign = () => {
  const navigate = useNavigate();

  // Fetch options
  const { data: catResp, isLoading: catsLoading } = useGetAllCategoriesQuery({
    page: 1, limit: 200, search: "",
  });
  const { data: prodResp, isLoading: prodsLoading } = useGetAllProductsQuery({
    page: 1, limit: 500, search: "",
  });

  const categories = catResp?.data || [];
  const products = prodResp?.data || [];

  const [addDesign, { isLoading, error, isSuccess, data }] = useAddDesignMutation();

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting, isDirty },
    setFocus,
  } = useForm({
    defaultValues: {
      category: null,
      product: null,
      design: "",
      label: "",
      slug: "",
      description: "",
    },
    mode: "onBlur",
  });

  const designRef = useRef("");
  const slugTouchedRef = useRef(false);
  const mountedRef = useRef(false);

  // Focus "Design" on mount
  useEffect(() => {
    const id = requestAnimationFrame(() => setFocus("design"));
    return () => cancelAnimationFrame(id);
  }, [setFocus]);

  // Build options
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c._id, label: c.name })),
    [categories]
  );

  const selectedCategory = watch("category");
  const productOptions = useMemo(() => {
    const catId = selectedCategory?.value;
    return products
      .filter((p) => !catId || String(p.categoryId) === String(catId))
      .map((p) => ({
        value: p._id || p.id,
        label: p.productName || p.name,
        __categoryId: p.categoryId,
      }));
  }, [products, selectedCategory]);

  // When category changes, clear product WITHOUT validating right away
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true; // skip first render
      return;
    }
    setValue("product", null, { shouldValidate: false, shouldDirty: true });
    clearErrors("product"); // remove red state while disabled
  }, [selectedCategory?.value, setValue, clearErrors]);

  // ✅ Success: show toast, reset form, focus design (NO redirect)
  useEffect(() => {
    if (isSuccess && data?.success) {
      toast.success("Design added successfully!");
      reset(); // back to defaultValues
      slugTouchedRef.current = false;
      requestAnimationFrame(() => setFocus("design"));
    }
  }, [isSuccess, data, reset, setFocus]);

  // Error toast
  useEffect(() => {
    if (error) {
      const msg =
        error?.data?.msg || error?.data?.message || error?.error || "Error adding design!";
      toast.error(msg);
      requestAnimationFrame(() => setFocus("design"));
    }
  }, [error, setFocus]);

  // Auto-generate slug from design (unless user edited slug manually)
  const designValue = watch("design");
  const slugValue = watch("slug");
  useEffect(() => {
    const makeSlug = (s) =>
      s
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");

    const handler = () => {
      slugTouchedRef.current = true;
    };

    if (!slugTouchedRef.current) {
      const derived = makeSlug(designValue || "");
      if (!slugValue || slugValue === makeSlug(designRef.current || "")) {
        setValue("slug", derived, { shouldValidate: true });
      }
    }

    designRef.current = designValue;

    const slugEl = document.querySelector('input[name="slug"]');
    slugEl?.addEventListener("input", handler, { once: true });
    return () => slugEl?.removeEventListener("input", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designValue]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "Backspace") {
        e.preventDefault();
        navigate(-1);
      }
      if ((e.ctrlKey && e.key.toLowerCase() === "s") || (e.key === "Enter" && e.ctrlKey)) {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate, handleSubmit]);

  const onSubmit = async (form) => {
    const payload = {
      design: (form.design || "").trim(),
      label: (form.label || "").trim().toUpperCase(),
      slug: (form.slug || "").trim(),
      description: (form.description || "").trim(),
      categoryId: form.category?.value,
      categoryName: form.category?.label,
      productId: form.product?.value,
      productName: form.product?.label,
    };
    try {
      await addDesign(payload).unwrap();
    } catch {}
  };

  const onReset = () => {
    reset();
    slugTouchedRef.current = false;
    requestAnimationFrame(() => setFocus("design"));
  };

  const loading = isLoading || isSubmitting || catsLoading || prodsLoading;

  return (
    <div className="px-4 py-4">
      {/* Compact Header */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to="/masters/design-master-list"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Design List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
              Create Design
            </h1>
            <p className="text-xs text-gray-500">
              Tips: <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">S</kbd> or{" "}
              <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Enter</kbd> to save,{" "}
              <kbd className="px-1 bg-gray-100 rounded border ml-2">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Back Spc</kbd> go to list
            </p>
          </div>
        </div>
      </div>

      {/* Card — compact, no internal scroll */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
      >
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
          {/* Design */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Design<span className="text-red-500">*</span>
            </label>
            <input
              {...register("design", {
                required: true,
                minLength: 2,
                maxLength: 120,
              })}
              aria-invalid={!!errors.design}
              placeholder="e.g. Lotus Filigree Pendant"
              autoComplete="off"
              className={`${baseInput} ${!!errors.design ? errRing : okRing}`}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Category<span className="text-red-500">*</span>
            </label>
            <Controller
              name="category"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={categoryOptions}
                  placeholder="Select category"
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(!!errors.category)}
                  isDisabled={catsLoading}
                />
              )}
            />
          </div>

          {/* Product (required only after category chosen) */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Product<span className="text-red-500">*</span>
            </label>
            <Controller
              name="product"
              control={control}
              rules={{
                validate: (v) => {
                  if (!selectedCategory) return true; // not required until category selected
                  return !!v || "required";
                },
              }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={productOptions}
                  placeholder={selectedCategory ? "Select product" : "Select a category first"}
                  isDisabled={!selectedCategory || prodsLoading}
                  classNamePrefix="react-select"
                  styles={makeSelectStyles(!!errors.product && !!selectedCategory)}
                />
              )}
            />
          </div>

          {/* Label */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Label
              {/* <span className="text-red-500">*</span> */}
              <span className="text-xs font-normal text-gray-400 ml-1">(4 chars, A–Z / 0–9)</span>
            </label>
            <input
              {...register("label", {
                required: false,
                minLength: 4,
                maxLength: 4,
                pattern: /^[A-Z0-9]{4}$/,
                onChange: (e) => {
                  e.target.value = e.target.value.toUpperCase().slice(0, 4);
                },
              })}
              aria-invalid={!!errors.label}
              placeholder="e.g. LP01"
              maxLength={4}
              autoComplete="off"
              className={`${baseInput} ${!!errors.label ? errRing : okRing} tracking-widest`}
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Slug</label>
            <input
              {...register("slug")}
              aria-invalid={false}
              placeholder="auto-generated from design (you can override)"
              autoComplete="off"
              name="slug"
              className={`${baseInput} ${okRing}`}
            />
          </div>

          {/* Description (full width on md+) */}
          <div className="md:col-span-2">
            <label className="block text-[13px] font-medium text-gray-700 mb-1">Description</label>
            <textarea
              {...register("description")}
              aria-invalid={false}
              rows={2}
              placeholder="Short description or internal notes (optional)"
              className={`${baseInput} ${okRing} resize-y`}
            />
          </div>

          {/* Actions */}
          <div className="md:col-span-2 flex flex-col sm:flex-row gap-2 pt-1">
            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="submit"
              disabled={loading}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
            >
              {loading ? "Saving…" : "Create Design"}
            </motion.button>

            <button
              type="button"
              onClick={onReset}
              disabled={loading || !isDirty}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-60"
              title="Reset the form"
            >
              Reset
            </button>

            <Link
              to="/masters/design-master-list"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              title="Cancel and go back"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateDesign;
